import { useEffect, useState, useRef, useCallback } from "react";
import { getRecommendations } from "../api";

// In-flight request dedupe shared by all hook instances: the layout uses this
// hook for the nav badge/popover while Performance uses it for the Action
// Center, and both mount around the same time — sharing the pending promise
// avoids fetching the same property twice. Entries are dropped once settled,
// so later mounts fetch fresh data.
const inflightRequests = new Map();

const getRecommendationsShared = (propertyId, force) => {
  if (force || !inflightRequests.has(propertyId)) {
    const request = getRecommendations(propertyId);
    const cleanup = () => {
      if (inflightRequests.get(propertyId) === request) {
        inflightRequests.delete(propertyId);
      }
    };
    request.then(cleanup, cleanup);
    inflightRequests.set(propertyId, request);
  }
  return inflightRequests.get(propertyId);
};

// Refetch broadcast: hook state is per-instance, so a refetch on Performance's
// instance (e.g. after a promo is created from a card) would otherwise leave
// the layout's instance — the nav badge — showing the stale count until a
// reload. Every mounted instance registers here; notifyRefetch fans a refetch
// out to all of them. Only the FIRST listener forces a fresh network request;
// the rest re-read the shared in-flight promise it just created, so the whole
// fan-out still costs one API call (EO-1834).
const refetchListeners = new Set();

const notifyRefetch = (propertyId) => {
  let isFirst = true;
  refetchListeners.forEach((listener) => {
    listener(propertyId, isFirst);
    isFirst = false;
  });
};

export const useActionCenterData = (selectedProperty) => {
  const [actionCenterData, setActionCenterData] = useState(null);
  const [actionCenterLoading, setActionCenterLoading] = useState(false);
  // True once this property's FIRST fetch has finished, success OR failure —
  // `loading`/`data` can't express this: before the fetch effect runs and
  // after a failed fetch both read false/null alike. Count displays (nav
  // badge, Action Center header) wait on both feeds' settled flags before
  // showing a number, so a partial v1-only count never flashes (EO-1834).
  // Deliberately latched: a same-property refetch does NOT reset it — data
  // keeps its previous value during a refetch, so counts hold steady instead
  // of blinking out, then update when the fresh read lands.
  const [actionCenterSettled, setActionCenterSettled] = useState(false);
  const loadedPropertyRef = useRef(null);

  const fetchData = useCallback(
    // `force` bypasses the already-loaded guard and state reset; `refresh`
    // decides whether to hit the network or re-read the shared in-flight
    // promise (see notifyRefetch). They coincide everywhere except the
    // broadcast fan-out, hence the default.
    (propertyId, { force = false, refresh = force } = {}) => {
      if (!propertyId) {
        setActionCenterData(null);
        setActionCenterLoading(false);
        setActionCenterSettled(false);
        loadedPropertyRef.current = null;
        return;
      }

      if (!force && loadedPropertyRef.current === propertyId) {
        return;
      }

      setActionCenterLoading(true);
      // Only a property's first load un-settles; see the state comment above.
      if (loadedPropertyRef.current !== propertyId) {
        setActionCenterSettled(false);
      }
      let isCancelled = false;

      getRecommendationsShared(propertyId, refresh)
        .then((res) => {
          if (isCancelled) return;
          setActionCenterData(res?.data);
          loadedPropertyRef.current = propertyId;
        })
        .catch(() => {
          if (isCancelled) return;
          setActionCenterData(null);
        })
        .finally(() => {
          if (!isCancelled) {
            setActionCenterLoading(false);
            setActionCenterSettled(true);
          }
        });

      return () => {
        isCancelled = true;
      };
    },
    []
  );

  useEffect(() => {
    return fetchData(selectedProperty?.propertyId);
  }, [selectedProperty?.propertyId, fetchData]);

  // Join the refetch broadcast for as long as this instance is mounted. Each
  // instance only reacts to its own property, so a stray notify for another
  // hotel is a no-op.
  useEffect(() => {
    const listener = (propertyId, shouldRefresh) => {
      if (propertyId === selectedProperty?.propertyId) {
        fetchData(propertyId, { force: true, refresh: shouldRefresh });
      }
    };
    refetchListeners.add(listener);
    return () => {
      refetchListeners.delete(listener);
    };
  }, [selectedProperty?.propertyId, fetchData]);

  // Refetches EVERY mounted instance (nav badge + Action Center), not just
  // this one — a consumed recommendation must disappear from both counts.
  const refetch = useCallback(() => {
    const propertyId = selectedProperty?.propertyId;
    if (propertyId) {
      notifyRefetch(propertyId);
    }
  }, [selectedProperty?.propertyId]);

  return {
    actionCenterData,
    actionCenterLoading,
    actionCenterSettled,
    refetch,
  };
};
